import { fetchGlobalCryptoStats } from '@/lib/globalStats.functions';
import { supabase } from '@/integrations/supabase/client';
import { fetchCryptoMarkets } from '@/lib/markets.functions';
import type { Database } from '@/integrations/supabase/types';

type CryptoToken = Database['public']['Tables']['crypto_tokens']['Row'];
type TokenPriceHistory = Database['public']['Tables']['token_price_history']['Row'];
type UserWatchlist = Database['public']['Tables']['user_watchlists']['Row'];
type PriceAlert = Database['public']['Tables']['price_alerts']['Row'];

export class CryptoDataService {
  private static instance: CryptoDataService;
  private realtimeChannel: any = null;

  static getInstance(): CryptoDataService {
    if (!CryptoDataService.instance) {
      CryptoDataService.instance = new CryptoDataService();
    }
    return CryptoDataService.instance;
  }

  // Trigger manual sync of crypto data
  async triggerDataSync(): Promise<{ success: boolean; message: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('sync-crypto-data');
      
      if (error) throw error;
      
      return {
        success: true,
        message: data?.message || 'Sync completed successfully'
      };
    } catch (error) {
      console.error('Error triggering data sync:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Sync failed'
      };
    }
  }

  // Fetch tokens with pagination and sorting
  async getTokens(
    page: number = 1,
    limit: number = 100,
    sortBy: 'market_cap_rank' | 'market_cap' | 'price_change_percentage_24h' | 'total_volume' = 'market_cap_rank',
    sortDirection: 'asc' | 'desc' = 'asc',
    category?: string
  ): Promise<{ data: CryptoToken[]; total: number; error?: string }> {
    try {
      let query = supabase
        .from('crypto_tokens')
        .select('*', { count: 'exact' });

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error, count } = await query
        .order(sortBy, { ascending: sortDirection === 'asc' })
        .range((page - 1) * limit, page * limit - 1);

      if (error) throw error;

      return {
        data: data || [],
        total: count || 0
      };
    } catch (error) {
      console.error('Error fetching tokens:', error);
      return {
        data: [],
        total: 0,
        error: error instanceof Error ? error.message : 'Failed to fetch tokens'
      };
    }
  }

  // Get single token with detailed information
  async getTokenById(tokenId: string): Promise<CryptoToken | null> {
    try {
      const { data, error } = await supabase
        .from('crypto_tokens')
        .select('*')
        .eq('id', tokenId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching token:', error);
      return null;
    }
  }

  // Search tokens by name or symbol
  async searchTokens(query: string): Promise<CryptoToken[]> {
    try {
      const { data, error } = await supabase
        .from('crypto_tokens')
        .select('*')
        .or(`name.ilike.%${query}%,symbol.ilike.%${query}%`)
        .order('market_cap_rank', { ascending: true, nullsFirst: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching tokens:', error);
      return [];
    }
  }

  // Search for token-related news in our database
  async searchTokenRelatedNews(tokenName: string, tokenSymbol: string): Promise<any[]> {
    try {
      const searchTerms = [tokenName.toLowerCase(), tokenSymbol.toLowerCase()];
      
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .or(
          searchTerms
            .map(term => `title.ilike.%${term}%,content.ilike.%${term}%,excerpt.ilike.%${term}%`)
            .join(',')
        )
        .order('published_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching token news:', error);
      return [];
    }
  }

  // Get token markets data from real API
  async getTokenMarkets(tokenId: string): Promise<any[]> {
    try {
      console.log('🔄 Fetching real market data for:', tokenId);
      
      const data = await fetchCryptoMarkets({ data: { tokenId } });

      if (data?.success && data?.markets) {
        console.log('✅ Received', data.markets.length, 'markets');
        return data.markets;
      }

      return [];
    } catch (error) {
      console.error('Error in getTokenMarkets:', error);
      return [];
    }
  }

  // Get price history for charts
  async getTokenPriceHistory(
    tokenId: string,
    timeframe: '1m' | '5m' | '1h' | '1d' = '1h',
    limit: number = 100
  ): Promise<TokenPriceHistory[]> {
    try {
      const { data, error } = await supabase
        .from('token_price_history')
        .select('*')
        .eq('token_id', tokenId)
        .eq('timeframe', timeframe)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []).reverse(); // Return chronological order for charts
    } catch (error) {
      console.error('Error fetching price history:', error);
      return [];
    }
  }

  // User watchlist management
  async getUserWatchlist(userId: string): Promise<(UserWatchlist & { token: CryptoToken })[]> {
    try {
      const { data, error } = await supabase
        .from('user_watchlists')
        .select(`
          *,
          token:crypto_tokens(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).filter((d): d is typeof d & { token: NonNullable<typeof d.token> } => d.token !== null);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      return [];
    }
  }

  async addToWatchlist(userId: string, tokenId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_watchlists')
        .insert({ user_id: userId, token_id: tokenId });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      return false;
    }
  }

  async removeFromWatchlist(userId: string, tokenId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_watchlists')
        .delete()
        .eq('user_id', userId)
        .eq('token_id', tokenId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      return false;
    }
  }

  // Price alerts
  async getUserPriceAlerts(userId: string): Promise<(PriceAlert & { token: CryptoToken })[]> {
    try {
      const { data, error } = await supabase
        .from('price_alerts')
        .select(`
          *,
          token:crypto_tokens(*)
        `)
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).filter((d): d is typeof d & { token: NonNullable<typeof d.token> } => d.token !== null);
    } catch (error) {
      console.error('Error fetching price alerts:', error);
      return [];
    }
  }

  async createPriceAlert(
    userId: string,
    tokenId: string,
    alertType: 'above' | 'below',
    targetPrice: number
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('price_alerts')
        .insert({
          user_id: userId,
          token_id: tokenId,
          alert_type: alertType,
          target_price: targetPrice
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error creating price alert:', error);
      return false;
    }
  }

  // Real-time updates
  subscribeToTokenUpdates(callback: (payload: any) => void) {
    if (this.realtimeChannel) {
      this.unsubscribeFromTokenUpdates();
    }

    this.realtimeChannel = supabase
      .channel('crypto-tokens-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crypto_tokens'
        },
        callback
      )
      .subscribe();

    return this.realtimeChannel;
  }

  unsubscribeFromTokenUpdates() {
    if (this.realtimeChannel) {
      supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
  }

  // Get trending tokens with composite score (price change + volume + market cap)
  async getTrendingTokens(limit: number = 10): Promise<CryptoToken[]> {
    try {
      const { data, error } = await supabase
        .from('crypto_tokens')
        .select('*')
        .not('price_change_percentage_24h', 'is', null)
        .not('total_volume', 'is', null)
        .not('market_cap', 'is', null)
        .gte('price_change_percentage_24h', 2)
        .lte('price_change_percentage_24h', 100)
        .gte('market_cap', 10000000); // Min $10M market cap

      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Calculate composite trending score for each token
      const scored = data.map(token => {
        // Price score: 50% weight, cap at 50% change
        const priceChange = Math.min(Math.abs(token.price_change_percentage_24h || 0), 50);
        const priceScore = priceChange / 50;

        // Volume score: 30% weight, cap at $100M
        const volume = Math.min(token.total_volume || 0, 100000000);
        const volumeScore = volume / 100000000;

        // Market cap score: 20% weight, logarithmic scale
        const marketCap = token.market_cap || 1;
        const marketCapWeight = Math.min(Math.log10(marketCap / 10000000) / 3, 1);

        // Calculate composite score
        const trendingScore = (priceScore * 0.5) + (volumeScore * 0.3) + (marketCapWeight * 0.2);

        return {
          ...token,
          trendingScore
        };
      });

      // Sort by trending score and return top N
      return scored
        .sort((a, b) => b.trendingScore - a.trendingScore)
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching trending tokens:', error);
      return [];
    }
  }

  // Get token categories
  async getTokenCategories(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('crypto_tokens')
        .select('category')
        .not('category', 'is', null);

      if (error) throw error;
      
      const categories = [...new Set(data?.map(item => item.category).filter(Boolean))];
      return categories.sort();
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  // Get token-specific news
  async getTokenNews(tokenId: string, limit: number = 20) {
    try {
      const { data, error } = await supabase
        .from('token_news')
        .select(`
          *,
          article:articles(*)
        `)
        .eq('token_id', tokenId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching token news:', error);
      return [];
    }
  }

  // Get market overview data from ALL tokens in database
  async getMarketOverview(): Promise<{
    totalMarketCap: number;
    total24hVolume: number;
    btcDominance: number;
    activeTokens: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('crypto_tokens')
        .select('market_cap, total_volume, symbol')
        .not('market_cap', 'is', null)
        .not('total_volume', 'is', null);

      if (error) throw error;

      const totalMarketCap = data?.reduce((sum, token) => sum + (token.market_cap || 0), 0) || 0;
      const total24hVolume = data?.reduce((sum, token) => sum + (token.total_volume || 0), 0) || 0;
      const btcMarketCap = data?.find(token => token.symbol?.toLowerCase() === 'btc')?.market_cap || 0;
      const btcDominance = totalMarketCap > 0 ? (btcMarketCap / totalMarketCap) * 100 : 0;

      return {
        totalMarketCap,
        total24hVolume,
        btcDominance,
        activeTokens: data?.length || 0
      };
    } catch (error) {
      console.error('Error fetching market overview:', error);
      return {
        totalMarketCap: 0,
        total24hVolume: 0,
        btcDominance: 0,
        activeTokens: 0
      };
    }
  }

  // Fetch global crypto market stats - uses database fallback to avoid API rate limits
  async getGlobalMarketStats(): Promise<{
    total_market_cap: number;
    total_volume_24h: number;
    btc_dominance: number;
    eth_dominance: number;
    market_cap_change_24h: number;
    volume_change_24h: number;
    active_cryptocurrencies: number;
    last_updated: string;
  } | null> {
    try {
      // First, try to calculate from database (avoids API rate limits)
      const dbStats = await this.getMarketOverview();
      
      if (dbStats.activeTokens > 0) {
        // We have database data, use it
        return {
          total_market_cap: dbStats.totalMarketCap,
          total_volume_24h: dbStats.total24hVolume,
          btc_dominance: dbStats.btcDominance,
          eth_dominance: 0, // Calculate if needed
          market_cap_change_24h: 0, // Would need historical data
          volume_change_24h: 0, // Would need historical data
          active_cryptocurrencies: dbStats.activeTokens,
          last_updated: new Date().toISOString()
        };
      }

      // Fallback to API only if database is empty
      const data = await fetchGlobalCryptoStats();
      
      if (!data?.success) {
        console.warn('API unavailable, using database fallback with zero values');
        // Return zero values as last resort
        return {
          total_market_cap: 0,
          total_volume_24h: 0,
          btc_dominance: 0,
          eth_dominance: 0,
          market_cap_change_24h: 0,
          volume_change_24h: 0,
          active_cryptocurrencies: 0,
          last_updated: new Date().toISOString()
        };
      }
      
      return data.data ?? null;
    } catch (error) {
      console.error('Error in getGlobalMarketStats:', error);
      // Return database stats as fallback
      const dbStats = await this.getMarketOverview();
      return {
        total_market_cap: dbStats.totalMarketCap,
        total_volume_24h: dbStats.total24hVolume,
        btc_dominance: dbStats.btcDominance,
        eth_dominance: 0,
        market_cap_change_24h: 0,
        volume_change_24h: 0,
        active_cryptocurrencies: dbStats.activeTokens,
        last_updated: new Date().toISOString()
      };
    }
  }
}

export const cryptoDataService = CryptoDataService.getInstance();
